import { useCallback, useEffect, useState } from 'react';

import { Phone } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useCallEligibility, useInitiateCall } from '@/hooks/useOperatorCall';
import { cn } from '@/lib/utils';
import { useActiveCallStore } from '@/stores/active-call.store';

// ── Types ──────────────────────────────────────────────────────────

type Channel = 'pstn' | 'whatsapp';

interface OperatorCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactId: string;
  contactName: string;
  contactPhone: string;
  configurationId: string | undefined;
  defaultChannel?: Channel;
  overridePermission?: boolean;
}

// ── Helpers ────────────────────────────────────────────────────────

const formatCooldown = (isoDate: string, now: number): string => {
  const diff = new Date(isoDate).getTime() - now;
  if (diff <= 0) return '';
  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  if (hours > 0) return `Available in ${hours}h ${minutes}m`;
  return `Available in ${minutes}m`;
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

// ── Component ──────────────────────────────────────────────────────

export const OperatorCallDialog = ({
  open,
  onOpenChange,
  contactId,
  contactName,
  contactPhone,
  configurationId,
  defaultChannel,
  overridePermission,
}: OperatorCallDialogProps) => {
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(defaultChannel ?? null);

  const { data: eligibility } = useCallEligibility(
    open ? contactId : undefined,
    open ? configurationId : undefined,
  );
  const initiateMutation = useInitiateCall();
  const startCall = useActiveCallStore((s) => s.startCall);
  const isCallActive = useActiveCallStore((s) => s.isActive);

  const waPermission = eligibility?.whatsappCallPermission ?? 'none';
  const hasCallPermission = waPermission === 'temporary' || waPermission === 'permanent';

  const handleConfirm = useCallback(async () => {
    if (!selectedChannel || !configurationId) return;

    try {
      const shouldOverride =
        overridePermission || (selectedChannel === 'whatsapp' && !hasCallPermission);
      const result = await initiateMutation.mutateAsync({
        contactId,
        configurationId,
        channel: selectedChannel,
        overridePermission: shouldOverride || undefined,
      });

      // Hand off to the floating call window via the store
      startCall({
        contactId,
        contactName,
        contactPhone,
        configurationId,
        channel: selectedChannel,
        callId: result.callId,
        callSid: result.callSid,
        wsPath: result.wsPath,
        wsToken: result.wsToken,
      });

      // Close the confirmation dialog
      onOpenChange(false);
    } catch {
      // Error is shown by the mutation state
    }
  }, [
    selectedChannel,
    configurationId,
    contactId,
    contactName,
    contactPhone,
    initiateMutation,
    startCall,
    onOpenChange,
    overridePermission,
    hasCallPermission,
  ]);

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const pstnCooldown = eligibility?.pstnCooldownUntil
    ? new Date(eligibility.pstnCooldownUntil).getTime() > now
      ? formatCooldown(eligibility.pstnCooldownUntil, now)
      : ''
    : '';

  const whatsappCooldown = eligibility?.whatsappCooldownUntil
    ? new Date(eligibility.whatsappCooldownUntil).getTime() > now
      ? formatCooldown(eligibility.whatsappCooldownUntil, now)
      : ''
    : '';

  const showPstn = eligibility?.canCallPstn || !!pstnCooldown;

  const waPermissionExpiry = eligibility?.whatsappCallPermissionExpiresAt;
  const hasWhatsappConfig =
    eligibility?.canCallWhatsapp || !!whatsappCooldown || waPermission !== 'none';
  const showWhatsapp = hasWhatsappConfig;

  const formatPermissionExpiry = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="wa-call-dialog gap-0 overflow-hidden border-0 p-0 sm:max-w-[420px] sm:rounded-2xl"
        style={{ border: 'none' }}
      >
        <DialogTitle className="sr-only">Call {contactName}</DialogTitle>
        <DialogDescription className="sr-only">
          Choose a channel and confirm the call
        </DialogDescription>

        <div className="wa-call-confirm flex flex-col items-center px-8 pt-10 pb-8">
          {/* Avatar */}
          <div className="wa-avatar-ring relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#00a884]/20">
              <span className="wa-text-primary text-2xl font-bold">{getInitials(contactName)}</span>
            </div>
          </div>

          {/* Contact info */}
          <h3 className="wa-text-primary mt-5 text-xl font-semibold">{contactName}</h3>
          <p className="wa-text-muted mt-1 text-sm">{contactPhone}</p>

          {/* Already in a call warning */}
          {isCallActive && (
            <p className="mt-4 text-center text-xs text-amber-400">
              A call is already in progress. End it before starting a new one.
            </p>
          )}

          {/* Channel selector */}
          <div className="mt-7 flex w-full gap-3">
            {showPstn && (
              <button
                type="button"
                disabled={!!pstnCooldown}
                onClick={() => setSelectedChannel('pstn')}
                className={cn(
                  'wa-channel-btn flex flex-1 flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-xs transition-all',
                  selectedChannel === 'pstn'
                    ? 'border-[#00a884] bg-[#00a884]/10'
                    : 'wa-channel-btn-idle border-transparent',
                  pstnCooldown && 'cursor-not-allowed opacity-50',
                )}
              >
                <Phone className="h-5 w-5" />
                <span className="font-medium">Phone Call</span>
                {pstnCooldown && <span className="text-[10px] text-amber-500">{pstnCooldown}</span>}
              </button>
            )}
            {showWhatsapp && !hasCallPermission && (
              <button
                type="button"
                onClick={() => setSelectedChannel('whatsapp')}
                className={cn(
                  'wa-channel-btn flex flex-1 flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-xs transition-all',
                  selectedChannel === 'whatsapp'
                    ? 'border-[#f59e0b] bg-[#f59e0b]/10'
                    : 'wa-channel-btn-idle border-transparent',
                )}
              >
                <Phone className="h-5 w-5 text-[#f59e0b]" />
                <span className="font-medium text-[#f59e0b]">Call anyway</span>
                <span className="text-[10px] text-[#8696a0]">
                  {waPermission === 'requested' ? 'Permission pending' : 'No permission'}
                </span>
              </button>
            )}
            {showWhatsapp && hasCallPermission && (
              <button
                type="button"
                disabled={!!whatsappCooldown}
                onClick={() => setSelectedChannel('whatsapp')}
                className={cn(
                  'wa-channel-btn flex flex-1 flex-col items-center gap-2 rounded-xl border-2 px-4 py-4 text-xs transition-all',
                  selectedChannel === 'whatsapp'
                    ? 'border-[#00a884] bg-[#00a884]/10'
                    : 'wa-channel-btn-idle border-transparent',
                  whatsappCooldown && 'cursor-not-allowed opacity-50',
                )}
              >
                <Phone className="h-5 w-5" />
                <span className="font-medium">
                  {waPermission === 'temporary' && waPermissionExpiry
                    ? `WhatsApp Call (expires ${formatPermissionExpiry(waPermissionExpiry)})`
                    : 'WhatsApp Call'}
                </span>
                {whatsappCooldown && (
                  <span className="text-[10px] text-amber-500">{whatsappCooldown}</span>
                )}
              </button>
            )}
          </div>

          {!showPstn && !showWhatsapp && (
            <p className="wa-text-muted mt-6 text-center text-xs">
              No outbound calling numbers configured.
            </p>
          )}

          {/* Confirm button */}
          <button
            type="button"
            disabled={!selectedChannel || initiateMutation.isPending || isCallActive}
            onClick={() => void handleConfirm()}
            className={cn(
              'mt-7 w-full rounded-xl px-4 py-3 text-sm font-semibold transition-all',
              selectedChannel && !initiateMutation.isPending && !isCallActive
                ? 'wa-call-btn-active cursor-pointer'
                : 'wa-call-btn-disabled cursor-not-allowed',
            )}
          >
            {initiateMutation.isPending ? 'Starting...' : `Call ${contactName}`}
          </button>

          {initiateMutation.isError && (
            <p className="mt-3 text-center text-xs text-red-400">
              {initiateMutation.error instanceof Error
                ? initiateMutation.error.message
                : 'Failed to initiate call.'}
            </p>
          )}

          <p className="wa-text-dim mt-4 text-center text-[11px]">This call will be recorded</p>
        </div>
      </DialogContent>

      {/* WhatsApp call styles */}
      <style>{`
        /* ── Theme-aware variables ─────────────────────────── */
        .wa-call-dialog {
          --wa-bg-from: #0b3d2e;
          --wa-bg-to: #111b21;
          --wa-text: #e9edef;
          --wa-text-muted: #8696a0;
          --wa-text-dim: #5a6a75;
          --wa-surface: rgba(42, 57, 66, 0.6);
          --wa-channel-bg: rgba(42, 57, 66, 0.4);
        }

        :root:not(.dark) .wa-call-dialog {
          --wa-bg-from: #0b3d2e;
          --wa-bg-to: #111b21;
          --wa-text: #e9edef;
          --wa-text-muted: #8696a0;
          --wa-text-dim: #5a6a75;
          --wa-surface: rgba(42, 57, 66, 0.6);
          --wa-channel-bg: rgba(42, 57, 66, 0.4);
        }

        /* ── Backgrounds ───────────────────────────────────── */
        .wa-call-confirm {
          background: linear-gradient(180deg, var(--wa-bg-from) 0%, var(--wa-bg-to) 100%);
        }

        /* ── Text ─────────────────────────────────────────── */
        .wa-text-primary { color: var(--wa-text); }
        .wa-text-muted { color: var(--wa-text-muted); }
        .wa-text-dim { color: var(--wa-text-dim); }

        /* ── Channel buttons ──────────────────────────────── */
        .wa-channel-btn {
          color: var(--wa-text);
        }
        .wa-channel-btn-idle {
          background: var(--wa-channel-bg);
        }

        /* ── Call button ──────────────────────────────────── */
        .wa-call-btn-active {
          background-color: #00a884;
          color: #111b21;
        }
        .wa-call-btn-active:hover {
          background-color: #00c49a;
        }
        .wa-call-btn-disabled {
          background-color: var(--wa-surface);
          color: var(--wa-text-dim);
        }

        /* ── Avatar ring on confirm screen ─────────────────── */
        .wa-avatar-ring::before {
          content: '';
          position: absolute;
          inset: -4px;
          border-radius: 50%;
          border: 2px solid rgba(0, 168, 132, 0.3);
        }

        /* ── Glassmorphism backdrop ────────────────────────── */
        .wa-call-dialog {
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
        }
      `}</style>
    </Dialog>
  );
};
